import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { toast } from "@/hooks/use-toast";
import { LiveNumber } from "@/components/LiveNumber";
import { calculIR } from "@/utils/fiscal";

const C = {
  navy: "#1B3A5C", teal: "#2A9D8F", bg: "#F4F8FC", white: "#FFFFFF",
  border: "#D6E4F0", muted: "#6B85A3", lightTeal: "#E8F5F4",
};

function formatEur(n: number) {
  return n.toLocaleString("fr-FR") + "\u00a0€";
}

function ProgressBar({ step, total }: { step: number; total: number }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {Array.from({ length: total }, (_, i) => i + 1).map((s) => (
        <div
          key={s}
          className="transition-all duration-300"
          style={{
            width: step === s ? 28 : 8,
            height: 8,
            borderRadius: 4,
            backgroundColor: step >= s ? C.teal : C.border,
          }}
        />
      ))}
    </div>
  );
}

function SliderInput({
  label,
  subtitle,
  value,
  min,
  max,
  step,
  unit,
  onChange,
}: {
  label: string;
  subtitle: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  onChange: (v: number) => void;
}) {
  return (
    <div>
      <label className="block text-lg font-bold mb-1 text-center" style={{ color: C.navy }}>
        {label}
      </label>
      <p className="text-sm text-center mb-6" style={{ color: C.muted }}>
        {subtitle}
      </p>

      <div className="text-center mb-6">
        <span className="text-4xl font-extrabold" style={{ color: C.teal }}>
          {value.toLocaleString("fr-FR")}
        </span>
        <span className="text-xl font-bold ml-1" style={{ color: C.muted }}>{unit}</span>
      </div>

      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full mb-4"
        style={{ accentColor: C.teal, cursor: "pointer", height: 44 }}
      />

      <div className="flex justify-between text-xs" style={{ color: C.muted }}>
        <span>{min.toLocaleString("fr-FR")} {unit}</span>
        <span>{max.toLocaleString("fr-FR")} {unit}</span>
      </div>

      {/* Sync text input */}
      <div className="flex items-center justify-center gap-2 mt-4">
        <input
          type="text"
          value={value.toLocaleString("fr-FR")}
          onChange={(e) => {
            const v = parseInt(e.target.value.replace(/\D/g, ""), 10);
            if (!isNaN(v)) onChange(Math.min(max, Math.max(min, v)));
          }}
          className="w-28 rounded-xl px-4 py-2 text-center font-bold outline-none"
          style={{
            fontSize: 18,
            border: `2px solid ${C.teal}`,
            color: C.navy,
            backgroundColor: C.lightTeal,
          }}
        />
        <span className="font-semibold" style={{ color: C.muted }}>{unit}</span>
      </div>
    </div>
  );
}

export default function Onboarding() {
  const { user } = useAuth();
  const { refetch: refetchProfile } = useProfile();
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0=welcome, 1-3=questions, 4=magic moment
  const [targetCa, setTargetCa] = useState(4500);
  const [charges, setCharges] = useState(800);
  const [tarifSeance, setTarifSeance] = useState(70);
  const [semainesTravaillees, setSemainesTravaillees] = useState(45);
  const [saving, setSaving] = useState(false);

  // Pré-remplir depuis le simulateur Landing si disponible
  useEffect(() => {
    try {
      const saved = localStorage.getItem("psygestion_landing_sim");
      if (!saved) return;
      const { tarif, patients, charges: annualCharges, semaines } = JSON.parse(saved);
      if (tarif) setTarifSeance(Math.min(150, Math.max(40, tarif)));
      if (annualCharges) setCharges(Math.min(5000, Math.max(0, Math.round(annualCharges / 12))));
      if (semaines) setSemainesTravaillees(Math.min(52, Math.max(30, semaines)));
      if (tarif && patients && semaines) {
        const estimatedMonthlyCa = Math.round((tarif * patients * semaines) / 12);
        setTargetCa(Math.min(15000, Math.max(1000, estimatedMonthlyCa)));
      }
    } catch {}
  }, []);

  // Magic moment: auto-redirect after 2s
  useEffect(() => {
    if (step !== 4) return;
    const t = setTimeout(() => navigate("/dashboard", { replace: true }), 2000);
    return () => clearTimeout(t);
  }, [step, navigate]);

  const handleFinish = async () => {
    if (!user) return;
    setSaving(true);
    try {
      // Sécurité : plan='découverte' + stripe_subscription_status='free' sont envoyés
      // explicitement (défense en profondeur). Le DEFAULT DB et la politique INSERT
      // enforce ces valeurs côté serveur — aucun trial ne peut s'activer ici.
      // Pour les profils existants, le REVOKE UPDATE sur ces colonnes empêche toute
      // escalade de plan via le client (seul le webhook Stripe peut les modifier).
      const { error } = await supabase.from("profils").upsert({
        user_id: user.id,
        plan: "découverte",
        stripe_subscription_status: "free",
        statut_fiscal: "micro-bnc",
        anciennete: "debutante",
        target_monthly_ca: targetCa,
        estimated_monthly_charges: charges,
        average_session_rate: tarifSeance,
        tarif_seance: tarifSeance,
        nb_patients_semaine: patientsParSemaine,
        semaines_travaillees: semainesTravaillees,
        has_completed_onboarding: true,
      } as any, { onConflict: "user_id" });

      if (error) {
        // Fallback : profil existant — le REVOKE UPDATE bloque les colonnes Stripe.
        // On met à jour uniquement les colonnes autorisées par le GRANT authenticated.
        const { error: e2 } = await supabase.from("profils").update({
          statut_fiscal: "micro-bnc",
          anciennete: "debutante",
          target_monthly_ca: targetCa,
          estimated_monthly_charges: charges,
          average_session_rate: tarifSeance,
          has_completed_onboarding: true,
        } as any).eq("user_id", user.id);
        if (e2) throw e2;
        localStorage.setItem("psygestion_onboarding", JSON.stringify({
          target_monthly_ca: targetCa,
          estimated_monthly_charges: charges,
          average_session_rate: tarifSeance,
          semaines_travaillees: semainesTravaillees,
        }));
      }

      // Refetch du profil AVANT de naviguer : garantit que ProtectedRoute trouvera
      // has_completed_onboarding=true et n'effectuera pas de redirection retour.
      await refetchProfile();

      setStep(4); // Magic moment → useEffect déclenche navigate("/dashboard") après 2s
    } catch (e: any) {
      if (import.meta.env.DEV) console.error("❌ Onboarding save error:", e);
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
      setSaving(false);
    }
  };

  // ── STEP 0 — Welcome ──
  if (step === 0) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center px-6 text-center"
        style={{ backgroundColor: C.navy }}
      >
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl mb-8 shadow-lg"
          style={{ backgroundColor: C.teal }}
        >
          🧠
        </div>
        <h1 className="text-3xl font-bold mb-4 leading-tight" style={{ color: C.white }}>
          Bonjour 👋
        </h1>
        <p className="text-base mb-12" style={{ color: "rgba(255,255,255,0.8)", maxWidth: 340 }}>
          3 questions — 90 secondes — votre diagnostic fiscal personnalisé
        </p>
        <button
          onClick={() => setStep(1)}
          className="px-10 py-4 rounded-2xl font-bold text-base transition-opacity hover:opacity-90 active:scale-95"
          style={{ backgroundColor: C.teal, color: C.white }}
        >
          Commencer →
        </button>
      </div>
    );
  }

  // ── STEP 4 — Magic Moment ──
  if (step === 4) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center px-6 text-center"
        style={{ backgroundColor: C.bg }}
      >
        <div className="mb-6">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto animate-spin"
            style={{
              border: `4px solid ${C.border}`,
              borderTopColor: C.teal,
            }}
          />
        </div>
        <h2 className="text-xl font-bold mb-2" style={{ color: C.navy }}>
          Calcul de votre itinéraire fiscal 2026 en cours...
        </h2>
        <p className="text-sm" style={{ color: C.muted }}>
          Analyse de {formatEur(targetCa * 12)} de CA annuel estimé
        </p>
      </div>
    );
  }

  // ── Computed preview for step 3 ──
  // Dériver le nb de patients/semaine depuis le CA cible et le tarif séance,
  // puis recalculer le CA annuel réel = patients × tarif × semaines.
  // Cela garantit que TOUTE modification (tarif OU semaines) met à jour le résumé.
  const patientsParSemaine = Math.max(1, Math.round(targetCa / (tarifSeance * 4.33)));
  const caAnnuel = patientsParSemaine * tarifSeance * semainesTravaillees;
  const cotisations = Math.round(caAnnuel * 0.234);
  const chargesAnnuelles = charges * 12;
  const beneficeImposable = Math.round(caAnnuel * 0.66); // abattement Micro-BNC 34%
  const ir = calculIR(beneficeImposable);
  const netAnnuel = Math.max(0, caAnnuel - cotisations - chargesAnnuelles - ir);
  const netMensuel = Math.round(netAnnuel / 12);

  // ── STEPS 1-3 ──
  const stepConfig = [
    {
      icon: "🚀",
      label: "Le Moteur",
      component: (
        <SliderInput
          label="Quel est votre objectif de CA mensuel ?"
          subtitle="Le chiffre d'affaires que vous visez chaque mois"
          value={targetCa}
          min={1000}
          max={15000}
          step={100}
          unit="€"
          onChange={setTargetCa}
        />
      ),
    },
    {
      icon: "⚙️",
      label: "Le Levier",
      component: (
        <SliderInput
          label="Estimez vos charges mensuelles"
          subtitle="Loyer, supervision, logiciels, formations, téléphone..."
          value={charges}
          min={0}
          max={5000}
          step={50}
          unit="€"
          onChange={setCharges}
        />
      ),
    },
    {
      icon: "🎯",
      label: "Le Rythme",
      component: (
        <>
          <SliderInput
            label="Quel est votre tarif moyen par séance ?"
            subtitle="Le prix que vous facturez pour une consultation"
            value={tarifSeance}
            min={40}
            max={150}
            step={5}
            unit="€"
            onChange={setTarifSeance}
          />
          <div className="mt-6">
            <SliderInput
              label="Combien de semaines travaillez-vous par an ?"
              subtitle="Congés, formations, arrêts inclus — moyenne : 45–47 semaines"
              value={semainesTravaillees}
              min={30}
              max={52}
              step={1}
              unit="sem."
              onChange={setSemainesTravaillees}
            />
          </div>
          {/* Preview on last step */}
          <div
            className="rounded-2xl px-5 py-4 mt-6 transition-all duration-200"
            style={{ backgroundColor: C.lightTeal, border: `1px solid ${C.teal}` }}
          >
            <div className="font-bold text-sm mb-3" style={{ color: C.teal }}>
              💡 Ce qu'il vous reste vraiment
            </div>
            <div className="font-bold mb-1" style={{ fontSize: 24, color: C.navy }}>
              <LiveNumber value={netMensuel} fmt={formatEur} baseColor={C.navy} />
              <span className="text-base font-semibold ml-1" style={{ color: C.muted }}>/mois net</span>
            </div>
            <p className="text-xs font-semibold mb-3" style={{ color: C.teal }}>
              Après CIPAV, charges et impôts
            </p>
            <div className="text-xs space-y-1" style={{ color: C.muted }}>
              <div className="flex justify-between">
                <span>CA annuel ({patientsParSemaine} pat./sem. × {formatEur(tarifSeance)})</span>
                <span className="font-semibold" style={{ color: C.navy }}>{formatEur(caAnnuel)}</span>
              </div>
              <div className="flex justify-between">
                <span>Cotisations CIPAV (23,4%)</span>
                <span>− {formatEur(cotisations)}</span>
              </div>
              <div className="flex justify-between">
                <span>Charges annuelles</span>
                <span>− {formatEur(chargesAnnuelles)}</span>
              </div>
              <div className="flex justify-between">
                <span>Impôt sur le revenu (estimé)</span>
                <span>− {formatEur(ir)}</span>
              </div>
              <div className="flex justify-between font-bold pt-1 border-t" style={{ borderColor: C.teal + "40", color: C.navy }}>
                <span>Revenu net annuel</span>
                <span>{formatEur(netAnnuel)}</span>
              </div>
            </div>
            <p className="text-[10px] mt-3" style={{ color: C.muted }}>
              Estimation Micro-BNC · Barème IR 2026 · {semainesTravaillees} semaines · Ne remplace pas un expert-comptable
            </p>
          </div>
        </>
      ),
    },
  ];

  const current = stepConfig[step - 1];
  const isLastStep = step === 3;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8" style={{ backgroundColor: C.bg }}>
      <div className="w-full" style={{ maxWidth: 480 }}>
        <div className="rounded-2xl shadow-md bg-white p-8" style={{ border: `1px solid ${C.border}` }}>
          <ProgressBar step={step} total={3} />

          <button
            onClick={() => setStep(step - 1)}
            className="text-xs mb-4 flex items-center gap-1"
            style={{ color: C.muted }}
          >
            ← Retour
          </button>

          <div className="flex items-center gap-2 mb-6">
            <span className="text-xl">{current.icon}</span>
            <span className="text-xs font-bold px-3 py-1 rounded-full" style={{ backgroundColor: C.lightTeal, color: C.teal }}>
              Étape {step}/3 — {current.label}
            </span>
          </div>

          {current.component}

          <button
            onClick={isLastStep ? handleFinish : () => setStep(step + 1)}
            disabled={isLastStep && saving}
            className="w-full py-3 rounded-xl text-white font-bold text-sm transition-opacity mt-8 hover:opacity-90 active:scale-95"
            style={{
              backgroundColor: C.teal,
              opacity: isLastStep && saving ? 0.4 : 1,
              cursor: isLastStep && saving ? "not-allowed" : "pointer",
            }}
          >
            {isLastStep
              ? saving ? "Enregistrement..." : "Voir mon diagnostic fiscal →"
              : "Continuer →"
            }
          </button>
        </div>
      </div>
    </div>
  );
}
